(function () {
  'use strict';

  if (customElements.get('delivery-estimate')) return;

  const PRAGUE_TIME_ZONE = 'Europe/Prague';
  const DISPATCH_CUTOFF_MINUTES = 14 * 60;

  const overrideClock = value => {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(value || '');
    if (!match) return null;
    return {
      date: new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
      hour: Number(match[4] || 12),
      minute: Number(match[5] || 0),
    };
  };

  const pragueClock = value => {
    const override = overrideClock(value);
    if (override) return override;

    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: PRAGUE_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .filter(part => part.type !== 'literal')
        .map(part => [part.type, part.value]),
    );

    return {
      date: new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  };

  // The order-to-delivery window uses the Czech warehouse working calendar.
  const easterSunday = year => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    return new Date(year, month - 1, day, 12);
  };

  const isBusinessDay = date => {
    if (date.getDay() === 0 || date.getDay() === 6) return false;
    const monthDay = `${date.getMonth() + 1}-${date.getDate()}`;
    if (['1-1', '5-1', '5-8', '7-5', '7-6', '9-28', '10-28', '11-17', '12-24', '12-25', '12-26'].includes(monthDay)) return false;
    const easter = easterSunday(date.getFullYear());
    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2);
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    return date.getTime() !== goodFriday.getTime() && date.getTime() !== easterMonday.getTime();
  };


  const addCalendarDays = (source, days) => {
    const date = new Date(source);
    date.setDate(date.getDate() + days);
    return date;
  };

  const addBusinessDays = (source, days) => {
    const date = new Date(source);
    let remaining = days;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (isBusinessDay(date)) remaining -= 1;
    }
    return date;
  };

  const localizedRange = (start, end, locale) => {
    const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
    const language = String(locale).toLowerCase().split('-')[0];
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();

    // Czech and Slovak need the month in genitive ("října", "októbra").
    // formatRange shortens it to a numeric month, so compose this form from
    // the end date's localized parts.
    if (sameMonth && (language === 'cs' || language === 'sk')) {
      const endParts = formatter.formatToParts(end);
      const month = endParts.find(part => part.type === 'month')?.value;
      return `${start.getDate()}.–${end.getDate()}. ${month}`;
    }

    if (typeof formatter.formatRange === 'function') return formatter.formatRange(start, end);
    return `${formatter.format(start)}–${formatter.format(end)}`;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.render();
      if (!this.dataset.now) this.refreshTimer = window.setInterval(() => this.render(), 60 * 1000);
    }

    disconnectedCallback() {
      if (this.refreshTimer) window.clearInterval(this.refreshTimer);
    }

    render() {
      const output = this.querySelector('[data-delivery-estimate]');
      const support = this.querySelector('[data-delivery-support]');
      const minDays = Number.parseInt(this.dataset.minDays, 10);
      const maxDays = Number.parseInt(this.dataset.maxDays, 10);
      if (!output || !Number.isFinite(minDays) || !Number.isFinite(maxDays)) return;

      try {
        const clock = pragueClock(this.dataset.now);
        const pastCutoff = (clock.hour * 60) + clock.minute >= DISPATCH_CUTOFF_MINUTES;
        const dispatchesNextBusinessDay = !isBusinessDay(clock.date) || pastCutoff;
        const addDeliveryDays = this.dataset.dayBasis === 'calendar' ? addCalendarDays : addBusinessDays;
        const range = localizedRange(
          addDeliveryDays(clock.date, minDays),
          addDeliveryDays(clock.date, maxDays),
          this.dataset.locale || document.documentElement.lang || 'en',
        );
        output.textContent = (this.dataset.template || '__DATE__').replace('__DATE__', range);
        if (support) {
          support.textContent = dispatchesNextBusinessDay
            ? this.dataset.supportAfterCutoff || support.textContent
            : this.dataset.supportBeforeCutoff || support.textContent;
        }
      } catch (_) {
        // Keep the server-rendered business-day fallback when Intl is absent.
      }
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
})();
